import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Camera } from 'types/camera';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';
import { useAPI } from 'hooks/api/useAPI';

type UseDeleteReferenceDeploymentsParams = {
  camera_id: string;
  deployment_ids: string[];
};

/** Remove reference deployments from a universal bridge camera. */
export function useDeleteReferenceDeployments({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  string[],
  APIError,
  UseDeleteReferenceDeploymentsParams,
  UpdateQueriesDataContext<Camera>
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ camera_id, deployment_ids }) => {
      const { data } = await client.delete(
        `/apps/${applicationID}/cameras/${camera_id}/reference_deployments`,
        {
          params: {
            deployment_ids,
          },
        }
      );

      return data;
    },
    {
      ...options,
      async onMutate(params) {
        await queryClient.cancelQueries(['cameras']);
        await queryClient.cancelQueries(['camera']);

        onMutate?.(params);

        const { camera_id, deployment_ids } = params;

        return updateQueriesData({
          queryClient,
          listQueryKey: ['cameras'],
          singleQueryKey: ['camera'],
          ids: [camera_id],
          updateData(camera) {
            const updatedCamera = structuredClone(camera);

            updatedCamera.reference_deployment_ids =
              updatedCamera.reference_deployment_ids.filter(
                (id) => !deployment_ids.some((deploymentId) => deploymentId === id)
              );

            return updatedCamera;
          },
        });
      },
      async onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['cameras']);
        queryClient.invalidateQueries(['camera']);
        queryClient.invalidateQueries(['deployments']);
      },
    }
  );
}

